import { cn } from '@bem-react/classname'
import { FC, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import packageJson from '~/package.json'

import { Memo } from '@/hoc/Memo'
import {
  appVersionSelector,
  lastDownloadCompanyResultSelector,
  lastExternalSyncDateSelector,
  lastSyncLicenseDateSelector,
} from '@/core/services/Maintenance/store/version.selector'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import { versionThunk } from '@/core/services/Maintenance/store/version.slice'
import useBackendError from '@/hooks/useBackendError'
import { roleSelector } from '@/modules/AccountPage/store/accont.selector'
import { RoleUserModel } from '@/core/services/Token/model/TokenModel'

import { Container } from '@/components/Container'

import './Footer.scss'
import { FALLBACK_VALUE } from '@/config/constants'

const cnFooter = cn('Footer')

export const Footer: FC = Memo(() => {
  const { t } = useTranslation('translation')
  const appVersion = useAppSelector(appVersionSelector)
  const lastSyncLicenseDate = useAppSelector(lastSyncLicenseDateSelector)
  const lastExternalSyncDate = useAppSelector(lastExternalSyncDateSelector)
  const lastDownloadCompanyResult = useAppSelector(
    lastDownloadCompanyResultSelector,
  )

  const dispatch = useAppDispatch()
  const [setBackendError] = useBackendError()
  const role = useAppSelector(roleSelector)

  const loadData = useCallback(async () => {
    try {
      await dispatch(versionThunk()).unwrap()
    } catch (errors) {
      setBackendError(errors)
    }
  }, [])

  useEffect(() => {
    if (role === RoleUserModel.SuperUser) {
      loadData()
    }
  }, [role])

  return (
    <footer className={cnFooter()}>
      <Container>
        <div className={cnFooter('line')}>
          <div className={cnFooter('item')}>
            <div className={cnFooter('text')}>{t('Contact Us')}:</div>
            <a
              href='mailto:info@dinamicagenerale.com'
              className={cnFooter('link')}
            >
              info@dinamicagenerale.com
            </a>
            <div className={cnFooter('version')}>v{packageJson.version}</div>
            {role === RoleUserModel.SuperUser && (
              <div className={cnFooter('version')}>v{appVersion}</div>
            )}
          </div>

          {role === RoleUserModel.SuperUser && (
            <div className={cnFooter('item')}>
              <div className={cnFooter('sync')}>
                <div className={cnFooter('sync-title')}>
                  {`${t('Sync company list')}:`}
                </div>
                <div className={cnFooter('sync-value')}>
                  {lastSyncLicenseDate || FALLBACK_VALUE}
                </div>
              </div>

              <div className={cnFooter('sync')}>
                <div className={cnFooter('sync-title')}>
                  {`${t('Last company download')}:`}
                </div>
                <div className={cnFooter('sync-value')}>
                  {lastExternalSyncDate || FALLBACK_VALUE}
                </div>
              </div>

              <div className={cnFooter('sync')}>
                <div className={cnFooter('sync-title')}>
                  {`${t('Last download status')}:`}
                </div>
                <div className={cnFooter('sync-value')}>
                  {lastDownloadCompanyResult || FALLBACK_VALUE}
                </div>
              </div>
            </div>
          )}

          <div className={cnFooter('item')}>
            <div className={cnFooter('payments')}>
              <div className={cnFooter('payment')}>
                <i className='fa-brands fa-2x fa-cc-mastercard'></i>
              </div>
              <div className={cnFooter('payment')}>
                <i className='fa-brands fa-2x fa-cc-visa'></i>
              </div>
            </div>

            <div className={cnFooter('text')}>
              {t('Via Mondadori, 15 - 46025 - Poggio Rusco (MN) - Italy')}
            </div>

            <div className={cnFooter('socials')}>
              <a
                href='https://www.facebook.com/dinamicagenerale/'
                target='_blank'
                className={cnFooter('social')}
              >
                <i className='fa-brands fa-facebook-square fa-lg'></i>
              </a>
              <a
                href='https://twitter.com/dinamicagen'
                target='_blank'
                className={cnFooter('social')}
              >
                <i className='fa-brands fa-twitter-square fa-lg'></i>
              </a>
              <a
                href='https://www.youtube.com/channel/UCeIO97a5jQQaPUVvjfu9Daw'
                target='_blank'
                className={cnFooter('social')}
              >
                <i className='fa-brands fa-youtube fa-lg'></i>
              </a>
              <a
                href='https://www.instagram.com/dinamica_generale/'
                target='_blank'
                className={cnFooter('social')}
              >
                <i className='fa-brands fa-instagram fa-lg'></i>
              </a>
              <a
                href='https://www.linkedin.com/company/dinamica-generale-s-p-a-/'
                target='_blank'
                className={cnFooter('social')}
              >
                <i className='fa-brands fa-linkedin fa-lg'></i>
              </a>
            </div>
          </div>
        </div>
      </Container>
    </footer>
  )
})
